import React, { useEffect, useState, useCallback } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, RefreshCw } from "lucide-react";

const STATUS_STYLES = {
  pending: "text-yellow-800 border-yellow-200 bg-yellow-50",
  approved: "text-blue-800 border-blue-200 bg-blue-50",
  refunded: "text-emerald-700 border-emerald-200 bg-emerald-50",
  rejected: "text-red-700 border-red-200 bg-red-50",
};

const AdminRefunds = () => {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ session_id: "", amount: "", reason: "", admin_notes: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status !== "all") params.set("status", status);
      const { data } = await api.get(`/admin/refunds?${params}`);
      setItems(data.items); setTotal(data.total);
    } catch (e) { toast.error("Failed to load refunds", { description: e.response?.data?.detail || "" }); }
    finally { setLoading(false); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.session_id.trim() || !form.reason.trim()) { toast.error("Session ID and reason required"); return; }
    setSaving(true);
    try {
      await api.post("/admin/refunds", { ...form, amount: form.amount ? Number(form.amount) : null });
      toast.success("Refund request created");
      setDialogOpen(false);
      setForm({ session_id: "", amount: "", reason: "", admin_notes: "" });
      load();
    } catch (e) { toast.error("Failed", { description: e.response?.data?.detail || "" }); }
    finally { setSaving(false); }
  };

  const updateStatus = async (r, ns) => {
    const notes = window.prompt(`Admin notes for ${ns} (optional):`, r.admin_notes || "");
    if (notes === null) return;
    try {
      await api.patch(`/admin/refunds/${r.id}/status`, { status: ns, admin_notes: notes });
      toast.success(`Refund ${ns}`);
      load();
    } catch (e) { toast.error("Failed", { description: e.response?.data?.detail || "" }); }
  };

  return (
    <AdminLayout title="Refunds" actions={
      <div className="flex gap-2">
        <Button data-testid="refund-refresh" onClick={load} variant="outline" className="rounded-none"><RefreshCw size={14} className="mr-2" /> Refresh</Button>
        <Button data-testid="refund-new-btn" onClick={() => setDialogOpen(true)} className="rounded-none bg-accent hover:bg-accent/90"><Plus size={14} className="mr-2" /> New refund</Button>
      </div>
    }>
      <div className="border border-border bg-card p-4 mb-6">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="refund-status-filter" className="rounded-none h-11 w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All refunds</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="refunded">Refunded</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="border border-border bg-card">
        {loading && <div className="p-8 text-center text-muted-foreground">Loading…</div>}
        {!loading && items.length === 0 && <div className="p-12 text-center text-sm text-muted-foreground" data-testid="refunds-empty">No refund requests yet.</div>}
        {!loading && items.map((r) => (
          <div key={r.id} data-testid={`refund-row-${r.id}`} className="p-4 border-b border-border last:border-b-0 grid grid-cols-1 md:grid-cols-[1fr_140px_140px_120px_200px] gap-3 items-center">
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{r.user_email || r.user_id || "Guest"}</div>
              <div className="text-xs text-muted-foreground truncate">Order: {r.session_id}</div>
              <div className="text-xs italic text-muted-foreground mt-1">{r.reason}</div>
            </div>
            <div className="text-sm">{r.currency?.toUpperCase()} {r.amount}</div>
            <div className="text-xs uppercase">{r.gateway}</div>
            <div>
              <span className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${STATUS_STYLES[r.status]}`}>{r.status}</span>
            </div>
            <div className="flex flex-wrap gap-1 justify-end">
              {r.status === "pending" && <>
                <button data-testid={`refund-approve-${r.id}`} onClick={() => updateStatus(r, "approved")} className="text-[11px] px-2 py-1 border border-border hover:border-accent">Approve</button>
                <button data-testid={`refund-reject-${r.id}`} onClick={() => updateStatus(r, "rejected")} className="text-[11px] px-2 py-1 border border-border hover:border-destructive text-destructive">Reject</button>
              </>}
              {r.status === "approved" && (
                <button data-testid={`refund-mark-refunded-${r.id}`} onClick={() => updateStatus(r, "refunded")} className="text-[11px] px-2 py-1 border border-emerald-300 hover:bg-emerald-50 text-emerald-700">Mark refunded</button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 text-xs text-muted-foreground">{total} refund request(s)</div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader><DialogTitle>New refund request</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="overline text-muted-foreground text-[10px]">Order (session id)</Label><Input data-testid="refund-session-id" className="rounded-none mt-1" value={form.session_id} onChange={(e) => setForm((f) => ({ ...f, session_id: e.target.value }))} /></div>
            <div><Label className="overline text-muted-foreground text-[10px]">Amount (leave blank for full)</Label><Input data-testid="refund-amount" type="number" step="0.01" className="rounded-none mt-1" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} /></div>
            <div><Label className="overline text-muted-foreground text-[10px]">Reason</Label><Textarea data-testid="refund-reason" rows={2} className="rounded-none mt-1" value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} /></div>
            <div><Label className="overline text-muted-foreground text-[10px]">Admin notes</Label><Textarea data-testid="refund-notes" rows={2} className="rounded-none mt-1" value={form.admin_notes} onChange={(e) => setForm((f) => ({ ...f, admin_notes: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button data-testid="refund-create-submit" className="rounded-none bg-accent hover:bg-accent/90" onClick={create} disabled={saving}>{saving ? "Creating…" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminRefunds;
